/**
 * Сцена песочницы — описание опыта, из которого движок строит мир (model.ts), а рисунок —
 * картинку (risunok.ts). Песочница и готовый опыт — одна и та же сцена: в готовом опыте
 * ученику разрешено менять только перечисленное в `разрешено` (план —
 * docs/pesochnica_mehanika_plan_2026-11-04.pdf).
 *
 * Единицы — СИ: метры, килограммы, секунды, ньютоны. Ось y — вверх, ось x — вправо.
 * Сцена — простой JSON: её можно сохранить в базу и открыть заново.
 */

export type Точка = [number, number]

export interface Мир {
  /** Ускорение свободного падения, м/с², направлено вниз. На Земле — 9,8. */
  g: number
}

/**
 * Неподвижная поверхность: стол, горка, стенка. `точки` — верхний край слева направо;
 * под ним сплошное тело толщиной `толщина` (до самой низкой точки минус толщина).
 * `скругление` — радиус, которым скругляются изломы: шар съезжает с горки на стол плавно,
 * а не ударяется о стык.
 */
export interface Поверхность {
  id: string
  точки: Точка[]
  материал: string
  /** Участки другого материала поверх основного — например, полоса песка на столе. */
  покрытия?: Покрытие[]
  толщина?: number
  скругление?: number
  подпись?: string
}

/**
 * Покрытие поверхности от x = `от` до x = `до`. Сделано покрытием, а не отдельной
 * поверхностью: на стыке двух поверхностей шар подпрыгивает, а покрытие стыка не имеет.
 * Трение и качение берутся от покрытия; удар (упругость) — от основного материала.
 */
export interface Покрытие {
  от: number
  до: number
  материал: string
}

interface ТелоОбщее {
  id: string
  /** Центр масс в начале опыта. */
  x: number
  y: number
  /** Поворот, радианы, против часовой стрелки. */
  угол?: number
  масса: number
  материал: string
  /** Начальная скорость. */
  v?: Точка
  /** Тело держит рука, пока его не отпустят (`Модель.отпустить`). */
  держать?: boolean
  подпись?: string
}

export interface Шар extends ТелоОбщее {
  форма: 'шар'
  радиус: number
}

export interface Брусок extends ТелоОбщее {
  форма: 'брусок'
  ширина: number
  высота: number
}

/** Тележка на двух колёсах; колёса без массы и без трения в осях — катятся свободно. */
export interface Тележка extends ТелоОбщее {
  форма: 'тележка'
  ширина: number
  /** Высота кузова (без колёс). */
  высота: number
  радиусКолеса: number
  /** Материал шин — для сопротивления качению; по умолчанию резина. */
  шины?: string
  /** Сколько гирь стоит на тележке — только для рисунка: их масса уже в `масса`. */
  гири?: number
}

export type Тело = Шар | Брусок | Тележка

/** Точка крепления: на теле (в его собственных координатах от центра) или в мире (тело = null). */
export interface Крепление {
  тело: string | null
  точка: Точка
}

/**
 * Пружина по закону Гука: F = k·|l − l₀|. `толькоТолкает` — пружина прикреплена к одному
 * телу и упирается в другое: толкает, пока сжата, и отходит, распрямившись.
 */
export interface Пружина {
  id: string
  вид: 'пружина'
  a: Крепление
  b: Крепление
  /** Жёсткость, Н/м. */
  k: number
  /** Длина без нагрузки, м. */
  длина: number
  толькоТолкает?: boolean
}

/** Нерастяжимая нить: тела не расходятся дальше её длины. Пережигается (`Модель.пережечь`). */
export interface Нить {
  id: string
  вид: 'нить'
  a: Крепление
  b: Крепление
  длина: number
}

export type Связь = Пружина | Нить

export interface Сцена {
  версия: 1
  название: string
  мир: Мир
  поверхности: Поверхность[]
  тела: Тело[]
  связи: Связь[]
  /** Что ученик может менять в готовом опыте (пути вида «тела.шар.масса»); пусто — песочница. */
  разрешено?: string[]
}

export const G_ЗЕМЛЯ = 9.8

// ---------------------------------------------------------------- скругление изломов

/**
 * Ломаная со скруглёнными изломами: каждый внутренний угол заменяется дугой радиуса r,
 * касательной к обоим отрезкам. Если отрезки короче, чем нужно дуге, радиус уменьшается.
 */
export function скруглить(точки: Точка[], r: number, шагУгла = (2 * Math.PI) / 180): Точка[] {
  if (r <= 0 || точки.length < 3) return точки.slice()
  const итог: Точка[] = [точки[0]]
  for (let i = 1; i < точки.length - 1; i++) {
    const [px, py] = точки[i - 1]
    const [cx, cy] = точки[i]
    const [nx, ny] = точки[i + 1]
    const d1 = Math.hypot(cx - px, cy - py)
    const d2 = Math.hypot(nx - cx, ny - cy)
    const u1: Точка = [(cx - px) / d1, (cy - py) / d1]
    const u2: Точка = [(nx - cx) / d2, (ny - cy) / d2]
    const поворот = Math.atan2(u1[0] * u2[1] - u1[1] * u2[0], u1[0] * u2[0] + u1[1] * u2[1])
    if (Math.abs(поворот) < 1e-6) {
      итог.push([cx, cy])
      continue
    }
    // расстояние от вершины до точки касания: r·tg(θ/2); не дальше половины отрезка
    let rr = r
    let отступ = rr * Math.tan(Math.abs(поворот) / 2)
    const предел = Math.min(d1, d2) / 2
    if (отступ > предел) {
      rr = (rr * предел) / отступ
      отступ = предел
    }
    const t1: Точка = [cx - u1[0] * отступ, cy - u1[1] * отступ]
    // центр дуги — по нормали к первому отрезку в сторону поворота
    const знак = Math.sign(поворот)
    const n1: Точка = [-u1[1] * знак, u1[0] * знак]
    const o: Точка = [t1[0] + n1[0] * rr, t1[1] + n1[1] * rr]
    const a0 = Math.atan2(t1[1] - o[1], t1[0] - o[0])
    const шагов = Math.max(2, Math.ceil(Math.abs(поворот) / шагУгла))
    for (let k = 0; k <= шагов; k++) {
      const a = a0 + (поворот * k) / шагов
      итог.push([o[0] + rr * Math.cos(a), o[1] + rr * Math.sin(a)])
    }
  }
  итог.push(точки[точки.length - 1])
  return итог
}

/** Верхний край поверхности с учётом скругления. */
export function верхПоверхности(п: Поверхность): Точка[] {
  return скруглить(п.точки, п.скругление ?? 0)
}

/** Материал поверхности в точке с абсциссой x (с учётом покрытий). */
export function материалПоверхности(п: Поверхность, x: number): string {
  for (const к of п.покрытия ?? []) if (x >= к.от && x <= к.до) return к.материал
  return п.материал
}

/** Кусок ломаной между абсциссами от и до (концы — на самой ломаной). */
export function участокЛоманой(точки: Точка[], от: number, до: number): Точка[] {
  const итог: Точка[] = []
  const на = (a: Точка, b: Точка, x: number): Точка => [x, a[1] + ((b[1] - a[1]) * (x - a[0])) / (b[0] - a[0])]
  for (let i = 0; i < точки.length - 1; i++) {
    const [a, b] = [точки[i], точки[i + 1]]
    const [лев, прав] = a[0] <= b[0] ? [a, b] : [b, a]
    if (прав[0] < от || лев[0] > до || прав[0] === лев[0]) continue
    const x0 = Math.max(лев[0], от)
    const x1 = Math.min(прав[0], до)
    const p0 = на(лев, прав, x0)
    if (итог.length === 0 || Math.hypot(итог[итог.length - 1][0] - p0[0], итог[итог.length - 1][1] - p0[1]) > 1e-9) итог.push(p0)
    итог.push(на(лев, прав, x1))
  }
  return итог
}

/** Нижний уровень сплошного тела поверхности. */
export function низПоверхности(п: Поверхность): number {
  return Math.min(...п.точки.map((т) => т[1])) - (п.толщина ?? 0.05)
}
